import { ParsedSunoOutput } from '@/types';
import type { SongSeed } from '@components/RiffWriter/types';

/**
 * Converts ParsedSunoOutput to markdown format for the Master Architect Module
 * This matches the HookHouse .md file format
 */
export function formatSongDataAsMarkdown(output: ParsedSunoOutput): string {
  const parts: string[] = [];

  // Title and Artist
  if (output.title && output.artist) {
    parts.push(`# ${output.title} // ${output.artist}`);
  } else if (output.title) {
    parts.push(`# ${output.title}`);
  }

  // Summary
  if (output.summary) {
    parts.push('', '## Summary', output.summary);
  }

  // Style Blueprint
  if (output.styleBlueprint) {
    parts.push('', '## Style (Production Blueprint)', output.styleBlueprint);
  }

  // Suno Tags
  if (output.style) {
    parts.push('', '## Suno Tags', '```', output.style, '```');
  }

  // Excluded Styles
  if (output.excludedStyle) {
    parts.push('', '## Excluded Styles', '```', output.excludedStyle, '```');
  }

  // Lyrics
  if (output.lyricsWithTags) {
    parts.push('', '## Lyrics', '```', output.lyricsWithTags, '```');
  }

  // JavaScript Code
  if (output.javascriptCode) {
    parts.push('', '## Suno Console JavaScript', '```javascript', output.javascriptCode, '```');
  }

  return parts.join('\n');
}

/**
 * Convert a RiffWriter SongSeed to the same markdown shape PromptArchitect
 * consumes. Seeds don't have lyrics — Art works from concept-level inputs,
 * which is exactly what a seed carries.
 */
export function formatSeedAsMarkdown(seed: SongSeed): string {
  const parts: string[] = [];
  parts.push(`# ${seed.title}`);
  if (seed.concept) parts.push('', '## Summary', seed.concept);

  const styleLines: string[] = [];
  if (seed.era) styleLines.push(`Era: ${seed.era}`);
  if (seed.vocalStyle) styleLines.push(`Vocal: ${seed.vocalStyle}`);
  if (seed.instruments?.length) styleLines.push(`Instruments: ${seed.instruments.join(', ')}`);
  if (seed.structure) styleLines.push(`Structure: ${seed.structure}`);
  if (seed.bpm) styleLines.push(`Tempo: ${seed.bpm} BPM`);
  if (seed.key) styleLines.push(`Key: ${seed.key}`);
  if (styleLines.length) {
    parts.push('', '## Style (Production Blueprint)', styleLines.join('\n'));
  }

  if (seed.povSetting) parts.push('', '## POV / Setting', seed.povSetting);
  if (seed.mustHaves?.length) parts.push('', '## Must Haves (Themes)', seed.mustHaves.map(t => `- ${t}`).join('\n'));
  if (seed.avoid?.length) parts.push('', '## Excluded Styles', '```', seed.avoid.join(', '), '```');
  if (seed.userTheme) parts.push('', '## User Directive', seed.userTheme);

  return parts.join('\n');
}
